// REALISTIC LAND MONSTERS (2026-10-23, owner directive "really realistic
// models"): GODZILLA, KONG, the ALIEN and the LOOT GOBLIN are ORIGINAL NEON
// PURGE assets, sculpted and rigged offline by agent/gen-creatures.mjs (signed-
// distance sculpt → decimated skinned mesh → baked PBR maps → keyframed clips),
// shipped as public/models/<kind>/<kind>.glb.
//
// Gameplay is untouched. The GLBs were generated at the procedural models'
// exact scale and frame (feet at y=0, facing -z, same heights), so they mount
// straight into MonsterMesh.group; the server-side hit boxes, the follow /
// dead-reckoning and the spiderweb 2.2x scale are all unchanged.
//
// Animation: one AnimationMixer per monster. The clip is chosen from the same
// replicated {mode, modeT} the procedural update() reads, and the walk cycle's
// playback rate tracks the monster's measured ground speed against the clip's
// own stride metadata (`speed`, in the GLB animation extras). The jaw is in the
// clips; the glow tells (godzilla's charging plates, the alien's eye) drive the
// per-instance material's emissiveIntensity from the same signals.
//
// Every machine but the TV app loads these (owner 2026-11-04: "the 3D models
// should always be the correct one" — gltfAssets realModelsEnabled); a failed
// load still keeps the procedural mesh (gltfAssets resolves null).
// Shared resources: the cached glTF's geometry and textures are tagged
// userData.shared (gltfAssets); each instance CLONES the material (so its glow
// can flare independently) and owns its Skeleton — both freed by MonsterMesh's
// dispose walker, which skips everything shared.
//
// NEVER call skeleton.pose() on these: meshopt quantization folds a scale into
// the inverse bind matrices, so pose() would shrink the model. The mixer only
// writes bone TRS, which is safe.
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { gltfIfReady, instantiate, loadGltf, realModelsEnabled } from "./gltfAssets.js";

export type CreatureKind = "godzilla" | "kong" | "alien" | "goblin";
export const CREATURE_KINDS: readonly CreatureKind[] = ["godzilla", "kong", "alien", "goblin"];
export const isCreatureKind = (k: string): k is CreatureKind => (CREATURE_KINDS as readonly string[]).includes(k);
export const creatureUrl = (k: CreatureKind): string => `/models/${k}/${k}.glb`;

/** Same gate as seaModels: off only on the TV app and outside a browser. */
export function creatureModelsEnabled(): boolean {
  return realModelsEnabled();
}

/** Start the fetch (map load). No-op when disabled. */
export function preloadCreature(k: CreatureKind): Promise<GLTF | null> {
  if (!creatureModelsEnabled()) return Promise.resolve(null);
  void gltfIfReady(creatureUrl(k)); // arm the synchronous ready-peek
  return loadGltf(creatureUrl(k));
}

export function creatureIfReady(k: CreatureKind): GLTF | null {
  return creatureModelsEnabled() ? gltfIfReady(creatureUrl(k)) : null;
}

interface ClipSet { clips: Map<string, THREE.AnimationClip>; walkSpeed: number; walkClip: string }
const clipSets = new WeakMap<GLTF, ClipSet>();
function clipSet(k: CreatureKind, g: GLTF): ClipSet | null {
  let c = clipSets.get(g);
  if (c) return c;
  const clips = new Map<string, THREE.AnimationClip>();
  for (const a of g.animations) clips.set(a.name, a);
  const walkClip = k === "goblin" ? "run" : "walk";
  const walk = clips.get(walkClip);
  if (!walk || !clips.get("idle") || !clips.get("death")) return null;
  // the generator writes {stride, speed} into the animation's glTF extras;
  // GLTFLoader drops those, but the parsed JSON keeps them (same order)
  const json = (g.parser as unknown as { json?: { animations?: { extras?: { speed?: number } }[] } }).json;
  const ex = json?.animations?.[g.animations.indexOf(walk)]?.extras;
  const walkSpeed = ex?.speed ?? (k === "goblin" ? 6.9 : k === "kong" ? 4.2 : k === "alien" ? 3.4 : 3.2);
  c = { clips, walkSpeed, walkClip };
  clipSets.set(g, c);
  return c;
}

export interface CreatureRig {
  kind: CreatureKind;
  root: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  actions: Map<string, THREE.AnimationAction>;
  cur: THREE.AnimationAction | null;
  curName: string;
  walkSpeed: number;
  walkClip: string;
  /** per-instance material (cloned so the glow flares per monster) */
  mats: THREE.MeshStandardMaterial[];
  glow: number;
}

const ONCE = new Set(["death", "fire_windup", "roar"]);

/** A rigged instance of the creature, or null when the asset is unusable. */
export function buildCreatureModel(k: CreatureKind, g: GLTF): CreatureRig | null {
  const cs = clipSet(k, g);
  if (!cs) return null;
  const root = instantiate(g);
  const mats: THREE.MeshStandardMaterial[] = [];
  root.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isMesh) return;
    m.castShadow = true;
    m.receiveShadow = true;
    // clips move limbs far outside the bind-pose bounds (the death topple, the
    // chest-beat): one big draw, never worth a wrong cull
    m.frustumCulled = false;
    const src = m.material as THREE.MeshStandardMaterial;
    const mat = src.clone();
    mat.userData = { ...src.userData, shared: false }; // ours: disposed with the monster (its maps stay shared)
    m.material = mat;
    mats.push(mat);
  });
  const mixer = new THREE.AnimationMixer(root);
  const actions = new Map<string, THREE.AnimationAction>();
  for (const [name, clip] of cs.clips) {
    const a = mixer.clipAction(clip);
    if (ONCE.has(name)) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
    actions.set(name, a);
  }
  const rig: CreatureRig = { kind: k, root, mixer, actions, cur: null, curName: "", walkSpeed: cs.walkSpeed, walkClip: cs.walkClip, mats, glow: 1 };
  play(rig, "idle", 0);
  // de-sync a crowd of the same kind (several goblins never step in phase)
  if (rig.cur) rig.cur.time = Math.random() * rig.cur.getClip().duration;
  mixer.update(0);
  return rig;
}

function play(r: CreatureRig, name: string, fade: number): void {
  if (r.curName === name) return;
  const next = r.actions.get(name) ?? r.actions.get("idle")!;
  next.reset();
  next.enabled = true;
  next.setEffectiveWeight(1);
  if (r.cur && fade > 0) next.crossFadeFrom(r.cur, fade, false);
  else if (r.cur) r.cur.stop();
  next.play();
  r.cur = next;
  r.curName = name;
}

/** The clip a mode maps to (same mode table as the procedural update()). */
function clipFor(k: CreatureKind, mode: number, modeT: number, speed: number, walkClip: string): string {
  if (mode === 9) return "death";
  const moving = speed > 0.35;
  if (k === "godzilla") {
    if (mode === 1) return modeT < 0.6 ? "fire_windup" : "fire";
    return mode === 0 && moving ? walkClip : "idle";
  }
  if (k === "kong") {
    if (mode === 2) return "climb";
    if (mode === 4) return "descend";
    if (mode === 3) return "chestbeat";
    return moving ? walkClip : "idle";
  }
  if (k === "alien") {
    if (mode === 1) return "fire";
    return moving ? walkClip : "idle";
  }
  // goblin: 0 run, 2 dig (burrow)
  if (mode === 2) return "dig";
  return moving ? walkClip : "idle";
}

/** Per-frame: clip choice, speed-scaled walk, glow flares. Allocation-free. */
export function updateCreatureModel(r: CreatureRig, t: number, dt: number, mode: number, modeT: number, speed: number): void {
  const name = clipFor(r.kind, mode, modeT, speed, r.walkClip);
  play(r, name, name === "death" ? 0.25 : name === "fire" ? 0.15 : 0.3);
  const a = r.cur!;
  if (name === r.walkClip) {
    // stride-matched: feet stay planted at any ground speed the server sends
    a.timeScale = Math.min(1.8, Math.max(0.45, speed / r.walkSpeed));
  } else a.timeScale = 1;
  // glow tells: godzilla's plates charge through the breath; the alien's eye
  // pulses and FLARES when it fires; otherwise the baked emissive at rest
  let glow = 1;
  if (r.kind === "godzilla" && mode === 1) glow = modeT < 0.6 ? 1 + modeT * 4 : 3.4 + Math.sin(t * 20) * 0.5;
  else if (r.kind === "alien") glow = mode === 1 ? 2.6 : 1 + Math.sin(t * 3) * 0.18;
  else if (mode === 9) glow = Math.max(0, 1 - modeT * 0.5);
  if (glow !== r.glow) {
    r.glow = glow;
    for (let i = 0; i < r.mats.length; i++) r.mats[i].emissiveIntensity = glow;
  }
  r.mixer.update(dt);
}
